import * as api from '@yandex-int/k-common/client/api';
import { SuggestedAddressesData } from '@yandex-int/k-common/client/api/defs/get-suggested-addresses';
import { Address } from '@yandex-int/k-common/typings';
import uniqueId from 'lodash/uniqueId';
import without from 'lodash/without';
import { showNetworkError } from 'platform-components';
import { getCertificates, getCertificateSuggestions } from 'platform-components/src/components/user/selectors';
import { delay, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { addViewedHint } from 'common.components/user/actions';

import * as actions from './actions';
import * as constants from './constants';
import { getCertificateStats } from './selectors';
import { CERTIFICATES, CERTIFICATE_SUGGESTIONS } from './typings';
import { updateCertificateStats } from './utils';

const DEBOUNCE_MS = 500;
const LOCALITIES_LIMIT = 10;

export function* certificateModalSagasWatcher() {
    yield takeLatest(constants.GET_LOCALITIES_REQUEST, getLocalitiesSaga);
    yield takeEvery(constants.ACCEPT_SUGGESTION_REQUEST, acceptSuggestion);
    yield takeLatest(constants.SET_CERTIFICATES_SECTION_REQUEST, setCertificatesSectionSaga);

    yield takeLatest(
        [constants.ACCEPT_SUGGESTION_SUCCESS, constants.CLOSE_SUGGESTION, constants.CANCEL_SUGGESTION],
        saveStats
    );
}

function* getLocalitiesSaga(action: ReturnType<typeof actions.getLocalities>) {
    yield delay(DEBOUNCE_MS);
    const { phrase } = action;
    try {
        let localities: Array<Address> = [];

        if (phrase) {
            const { items }: SuggestedAddressesData = yield call(api.getSuggestedAddresses, phrase);

            // eslint-disable-next-line no-return-assign
            items.forEach((item) => (item.id = uniqueId()));

            localities = items.slice(0, LOCALITIES_LIMIT);
        }

        yield put(actions.getLocalitiesSuccess(localities));
    } catch (error) {
        console.error(error);

        yield put(actions.getLocalitiesError(error));
        yield put(showNetworkError(error));
    }
}

function* acceptSuggestion(action: ReturnType<typeof actions.acceptSuggestion>) {
    const { certificateSlug, generateImmediately, data } = action;
    try {
        yield call(api.postCertificatesAccept, certificateSlug, generateImmediately, data);
        yield put(actions.acceptSuggestionSuccess(certificateSlug));
    } catch (error) {
        console.error(error);

        yield put(actions.acceptSuggestionError(error));
        yield put(showNetworkError(error));
    }
}

function* setCertificatesSectionSaga(action: ReturnType<typeof actions.setCertificateSectionRequest>) {
    const { certificateSlug } = action;
    try {
        const certificatesData: ReturnType<typeof getCertificates> = yield select(getCertificates);
        const certificateSuggestions: ReturnType<typeof getCertificateSuggestions> = yield select(
            getCertificateSuggestions
        );
        const nextCertificatesData = {
            ...certificatesData,
            [certificateSlug]: {},
            [CERTIFICATE_SUGGESTIONS]: without(certificateSuggestions, certificateSlug),
        };

        yield put(addViewedHint(CERTIFICATES, nextCertificatesData));
        yield put(actions.setCertificateSectionSuccess());
    } catch (error) {
        console.error(error);

        yield put(actions.setCertificateSectionError());
    }
}

// Сбор статистики
function* saveStats(
    action:
        | ReturnType<typeof actions.acceptSuggestion>
        | ReturnType<typeof actions.closeSuggestion>
        | ReturnType<typeof actions.cancelSuggestion>
) {
    const { certificateSlug } = action;
    const stats: ReturnType<typeof getCertificateStats> = yield select(getCertificateStats, { slug: certificateSlug });
    const certificatesData: ReturnType<typeof getCertificates> = yield select(getCertificates);
    const certificateSuggestions: ReturnType<typeof getCertificateSuggestions> = yield select(
        getCertificateSuggestions
    );

    const nextCertificatesData = {
        ...certificatesData,
        [certificateSlug]: updateCertificateStats(stats, action),
        [CERTIFICATE_SUGGESTIONS]: without(certificateSuggestions, certificateSlug),
    };

    yield put(addViewedHint(CERTIFICATES, nextCertificatesData));
}
